import { useAuth } from '@hb/react'
import {
  emptyUseQuery,
  Encounter,
  EncounterStatus,
  EncounterType,
  encounterTypeName,
  objectToArray,
  pregnancyEncounterTypes,
  UseQuery,
  WithId,
} from '@hb/shared'
import { createContext, PropsWithChildren, useContext, useMemo, useState } from 'react'
import { useGynCareView } from '../../../components/Gyn/context'
import { useProfile } from '../../../contexts/user/ProfileContext'
import { useExamListGroup } from '../context'
import { EncounterSelection } from '../types'

export interface EncounterListProviderProps<T extends EncounterType> {
  type: T
  onOpenExam: (exam: EncounterSelection) => void
  patientEncountersData: UseQuery<Encounter>
  practiceId: string | null
  patientId: string | null
}

export interface EncounterListContextData<T extends EncounterType>
  extends EncounterListProviderProps<T> {
  encounters: Record<string, Encounter<T>>
  encounterEntries: WithId<Encounter<T>>[]
  typeName: string
  dateAsc: boolean
  toggleDateAsc: () => void
}
export const EncountersListContext = createContext<EncounterListContextData<EncounterType>>({
  type: EncounterType.ANTEPARTUM,
  onOpenExam: () => {},
  practiceId: null,
  patientId: null,
  patientEncountersData: emptyUseQuery,
  encounters: {},
  encounterEntries: [],
  typeName: '',
  dateAsc: false,
  toggleDateAsc: () => {},
})

export const EncounterListProvider = <T extends EncounterType>({
  children,
  type,
  onOpenExam,
  patientEncountersData,
}: PropsWithChildren<EncounterListProviderProps<T>>) => {
  const { patientId } = useExamListGroup()
  const { practiceId: gynPracticeId } = useGynCareView()
  const {
    selectedAssessment: { populated: assessment },
    assessmentId,
  } = useProfile()

  const typeName = useMemo(() => encounterTypeName[type], [type])

  const practiceId = useMemo(() => {
    switch (type) {
      case EncounterType.ANTEPARTUM:
      case EncounterType.POSTPARTUM:
      case EncounterType.BIRTHING_CENTER_ADMISSION:
        return assessment?.midwifeId ?? null
      default:
        return gynPracticeId
    }
  }, [type, gynPracticeId, assessment])

  const { data: patientEncounters } = patientEncountersData

  const encounters = useMemo(
    () =>
      Object.entries(patientEncounters ?? {}).reduce<Record<string, Encounter<T>>>(
        (acc, [key, value]) => {
          if (pregnancyEncounterTypes.includes(type))
            return value.type === type && value.assessmentId === assessmentId
              ? { ...acc, [key]: value as Encounter<T> }
              : acc
          return value.type === type ? { ...acc, [key]: value as Encounter<T> } : acc
        },
        {},
      ),
    [patientEncounters, type, assessmentId],
  )

  const encountersArr = useMemo(() => objectToArray(encounters), [encounters])
  const authUser = useAuth(state => state.authUser)

  const [dateAsc, setDateAsc] = useState(type === EncounterType.ANTEPARTUM)

  const examEntries = useMemo(() => {
    const uid = authUser?.uid
    const allEncounters = encountersArr ?? []
    const entries = allEncounters.filter((encounter: Encounter<T>) => {
      if (encounter.type !== type) return false
      if (encounter.status === EncounterStatus.DRAFT) {
        return (
          !!uid && (encounter.renderingProvider === uid || encounter.supervisingProvider === uid)
        )
      }
      return true
    })
    return dateAsc ? [...entries].reverse() : entries
  }, [encountersArr, dateAsc, type, authUser])

  return (
    <EncountersListContext.Provider
      value={{
        type,
        typeName,
        onOpenExam,
        patientEncountersData,
        encounters,
        practiceId,
        encounterEntries: examEntries,
        dateAsc,
        toggleDateAsc: () => setDateAsc(prev => !prev),
        patientId,
      }}>
      {children}
    </EncountersListContext.Provider>
  )
}

export const useExamList = () => useContext(EncountersListContext)
